import type {
  Locale,
  MessageKey,
  StudentLifecycleEventContract,
  StudentLifecycleStatusContract,
  StudentProfileContract,
} from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Badge } from "@chams-pay/design-system/components/badge";
import { Button } from "@chams-pay/design-system/components/button";
import { Checkbox } from "@chams-pay/design-system/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@chams-pay/design-system/components/dialog";
import { GraduationCap, LogOut, PencilLine, RotateCcw } from "lucide-react";
import type {
  StudentLifecycleController,
  StudentLifecycleMode,
} from "../core/use-student-lifecycle.js";
import { DatePicker } from "../ui/date-picker.js";
import { FilterSelect } from "../ui/filter-select.js";
import { Amount, Bidi, DateValue } from "../ui/figures.js";

const modeTitle: Record<StudentLifecycleMode, MessageKey> = {
  withdrawal: "sp.withdrawStudent",
  graduation: "sp.graduateStudent",
  return: "sp.returnStudent",
  correction: "sp.correctLifecycle",
};

const modeHelp: Record<StudentLifecycleMode, MessageKey> = {
  withdrawal: "sp.lifecycleWithdrawalHelp",
  graduation: "sp.lifecycleGraduationHelp",
  return: "sp.lifecycleReturnHelp",
  correction: "sp.lifecycleCorrectionHelp",
};

const statusKey: Record<StudentLifecycleStatusContract, MessageKey> = {
  active: "sp.statusActive",
  withdrawn: "sp.statusWithdrawn",
  graduated: "sp.statusGraduated",
};

const eventKey: Record<StudentLifecycleEventContract["type"], MessageKey> = {
  withdrawal: "sp.lifecycleWithdrawal",
  graduation: "sp.lifecycleGraduation",
  return: "sp.lifecycleReturn",
  correction: "sp.lifecycleCorrected",
};

function failureKey(code: string | null): MessageKey | null {
  if (!code) return null;
  if (code === "PENDING_FUTURE_INSTRUMENT") return "sp.lifecyclePendingBlock";
  if (code === "LIFECYCLE_CORRECTION_CONFLICT") return "sp.lifecycleCorrectionConflict";
  if (code === "GRADUATION_NOT_ALLOWED") return "sp.lifecycleGraduationNotAllowed";
  return "sp.lifecycleFailed";
}

export function DesktopStudentLifecycle({
  locale,
  student,
  controller,
  editable,
}: {
  readonly locale: Locale;
  readonly student: StudentProfileContract;
  readonly controller: StudentLifecycleController;
  readonly editable: boolean;
}) {
  const t = (key: MessageKey) => translate(locale, key);
  const mode = controller.mode;
  const errorKey = failureKey(controller.failedCode);
  const prepaidReviewMad =
    student.lifecycleStatus === "active"
      ? "0"
      : student.obligations
          .filter(
            (obligation) =>
              obligation.dueDate > student.lifecycle.effectiveDate &&
              BigInt(obligation.settledMad) > 0n,
          )
          .reduce((total, obligation) => total + BigInt(obligation.settledMad), 0n)
          .toString();
  const statusOptions: StudentLifecycleStatusContract[] = student.lifecycle.graduationEligible
    ? ["active", "withdrawn", "graduated"]
    : ["active", "withdrawn"];

  return (
    <>
      <section className="student-section flex flex-col gap-3 rounded-lg border border-border bg-card px-5 py-4 shadow-e1">
        <div className="flex flex-wrap items-center gap-2">
          <div>
            <h3 className="font-heading text-[15px] font-bold">{t("sp.lifecycle")}</h3>
            <Badge
              dot
              variant={
                student.lifecycleStatus === "active"
                  ? "success"
                  : student.lifecycleStatus === "withdrawn"
                    ? "warning"
                    : "neutral"
              }
              className="mt-2"
            >
              {t(statusKey[student.lifecycleStatus])}
            </Badge>
          </div>
          {editable && (
            <div className="ms-auto flex flex-wrap justify-end gap-2">
              {student.lifecycleStatus === "active" ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => controller.start("withdrawal")}
                  >
                    <LogOut />
                    {t("sp.withdrawStudent")}
                  </Button>
                  {student.lifecycle.graduationEligible && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => controller.start("graduation")}
                    >
                      <GraduationCap />
                      {t("sp.graduateStudent")}
                    </Button>
                  )}
                </>
              ) : student.lifecycleStatus === "withdrawn" ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => controller.start("return")}
                >
                  <RotateCcw />
                  {t("sp.returnStudent")}
                </Button>
              ) : null}
              {student.lifecycle.events.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => controller.start("correction")}
                >
                  <PencilLine />
                  {t("sp.correctLifecycle")}
                </Button>
              )}
            </div>
          )}
        </div>

        {prepaidReviewMad !== "0" && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-warning-border bg-warning-soft px-3 py-2.5 text-xs text-warning">
            <strong>{t("sp.lifecyclePrepaid")}</strong>
            <strong>
              <Amount value={prepaidReviewMad} locale={locale} />
            </strong>
          </div>
        )}

        <div className="border-t border-border/60 pt-3">
          <h4 className="chams-eyebrow mb-2">{t("sp.lifecycleHistory")}</h4>
          {student.lifecycle.events.length ? (
            <div className="flex flex-col gap-2">
              {student.lifecycle.events.map((event) => (
                <div
                  key={event.id}
                  className="grid gap-1 rounded-md bg-surface-2 px-3 py-2 text-xs sm:grid-cols-[100px_1fr_auto]"
                >
                  <strong>
                    {t(eventKey[event.type])} · {t(statusKey[event.resultingStatus])}
                  </strong>
                  <span className="min-w-0">
                    {event.reason}
                    {event.correctsEventId && (
                      <small className="ms-2 text-muted-foreground">
                        · {t("sp.lifecycleCorrected")}
                      </small>
                    )}
                  </span>
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <DateValue value={event.effectiveDate} />
                    <span>·</span>
                    <Bidi>{locale === "ar" ? event.actorNameAr : event.actorNameFr}</Bidi>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">{t("sp.lifecycleNoHistory")}</p>
          )}
        </div>
      </section>

      <Dialog open={mode !== null} onOpenChange={(open) => !open && controller.close()}>
        <DialogContent closeLabel={t("common.close")} className="max-h-[90vh] overflow-y-auto p-6">
          {mode && (
            <form
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                void controller.submit();
              }}
            >
              <DialogHeader>
                <DialogTitle>{t(modeTitle[mode])}</DialogTitle>
                <DialogDescription>{t(modeHelp[mode])}</DialogDescription>
              </DialogHeader>

              <DatePicker
                locale={locale}
                value={controller.effectiveDate}
                min={controller.minimumDate}
                max={controller.maximumDate}
                label={t("sp.lifecycleEffectiveDate")}
                showLabel
                onChange={controller.setEffectiveDate}
              />

              {mode === "return" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1.5 text-xs font-bold">
                    {t("sp.lifecycleClass")}
                    <FilterSelect
                      value={controller.classLabel}
                      className="h-11"
                      options={controller.classOptions.map((classLabel) => ({
                        value: classLabel,
                        label: classLabel,
                      }))}
                      onChange={controller.setClassLabel}
                    />
                  </label>
                  {controller.sectionOptions.length > 0 && (
                    <label className="flex flex-col gap-1.5 text-xs font-bold">
                      {t("sp.section")}
                      <FilterSelect
                        value={controller.sectionLabel ?? ""}
                        className="h-11"
                        options={[
                          { value: "", label: "—" },
                          ...controller.sectionOptions.map((sectionLabel) => ({
                            value: sectionLabel,
                            label: sectionLabel,
                          })),
                        ]}
                        onChange={(value) =>
                          controller.setSectionLabel(value ? (value as "A" | "B") : null)
                        }
                      />
                    </label>
                  )}
                  <div className="flex flex-col gap-2 pt-5 text-xs font-semibold">
                    <div className="flex min-h-11 items-center gap-2">
                      <Checkbox
                        aria-label={t("sp.lifecycleTransport")}
                        checked={controller.transport}
                        onCheckedChange={controller.setTransport}
                      />
                      {t("sp.lifecycleTransport")}
                    </div>
                    <div className="flex min-h-11 items-center gap-2">
                      <Checkbox
                        aria-label={t("sp.lifecycleAccompaniment")}
                        checked={controller.accompaniment}
                        onCheckedChange={controller.setAccompaniment}
                      />
                      {t("sp.lifecycleAccompaniment")}
                    </div>
                  </div>
                </div>
              )}

              {mode === "correction" && (
                <label className="flex flex-col gap-1.5 text-xs font-bold">
                  {t("sp.lifecycleResultingStatus")}
                  <FilterSelect
                    value={controller.resultingStatus}
                    className="h-11"
                    options={statusOptions.map((status) => ({
                      value: status,
                      label: t(statusKey[status]),
                    }))}
                    onChange={(value) =>
                      controller.setResultingStatus(value as StudentLifecycleStatusContract)
                    }
                  />
                </label>
              )}

              <label className="flex flex-col gap-1.5 text-xs font-bold">
                {t("sp.lifecycleReason")}
                <textarea
                  required
                  minLength={3}
                  maxLength={500}
                  className="min-h-24 resize-y rounded-lg border border-input bg-card px-3 py-2.5 text-sm font-normal transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
                  value={controller.reason}
                  onChange={(event) => controller.setReason(event.target.value)}
                />
              </label>

              {(mode === "withdrawal" || mode === "graduation") && (
                <div className="grid gap-2 rounded-md border border-border bg-surface-2 p-3 text-xs">
                  {controller.previewing || !controller.impact ? (
                    <span className="text-muted-foreground">{t("sp.lifecyclePreviewing")}</span>
                  ) : (
                    <>
                      <span className="flex justify-between gap-3">
                        {t("sp.lifecycleRetainedDebt")}
                        <strong>
                          <Amount
                            value={controller.impact.retainedOutstandingMad}
                            locale={locale}
                          />
                        </strong>
                      </span>
                      <span className="flex justify-between gap-3">
                        {t("sp.lifecycleFutureCancelled")}
                        <strong>
                          <Amount value={controller.impact.futureCancellationMad} locale={locale} />
                        </strong>
                      </span>
                      {controller.impact.prepaidFutureMad !== "0" && (
                        <span className="flex justify-between gap-3 text-warning">
                          {t("sp.lifecyclePrepaid")}
                          <strong>
                            <Amount value={controller.impact.prepaidFutureMad} locale={locale} />
                          </strong>
                        </span>
                      )}
                      {controller.impact.pendingFutureMad !== "0" && (
                        <strong className="text-destructive">
                          {t("sp.lifecyclePendingBlock")}
                        </strong>
                      )}
                    </>
                  )}
                </div>
              )}

              <p className="rounded-md border border-warning-border bg-warning-soft px-3 py-2.5 text-xs leading-relaxed text-warning">
                {t("sp.lifecycleImmutable")}
              </p>
              <div className="flex min-h-11 items-start gap-2 text-xs font-semibold">
                <Checkbox
                  className="mt-0.5"
                  aria-label={t("sp.lifecycleConfirmation")}
                  checked={controller.confirmed}
                  onCheckedChange={controller.setConfirmed}
                />
                {t("sp.lifecycleConfirmation")}
              </div>
              {errorKey && (
                <p role="alert" className="text-xs text-destructive">
                  {t(errorKey)}
                </p>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={controller.close}>
                  {t("sp.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant={
                    mode === "withdrawal" || mode === "graduation" ? "destructive-solid" : "default"
                  }
                  disabled={controller.submitDisabled}
                >
                  {t("sp.lifecycleSubmit")}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
